import { buildPeriodSlots } from "../../Settings/Triggers/hooks/useTriggers";

/** Uppercase month abbreviations, as the period dropdown renders them. */
const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

/** Prefix per cadence for a numbered period, e.g. Q1 / H1. */
const PERIOD_PREFIX = { QUARTERLY: "Q", HALF_YEARLY: "H" };

/** Today as `YYYY-MM-DD`, matching the `max` the modal's date inputs carry. */
export const todayISO = () => new Date().toISOString().split("T")[0];

/**
 * The period a KPI's configured cadence says is due for reporting — what Add
 * Data prefills its date range with.
 *
 * The last period that has fully ended, which is the one a data owner has
 * complete figures for: on 10 Aug with an April fiscal start, a monthly KPI
 * lands on July and a quarterly one on Apr–Jun. Periods come from
 * `buildPeriodSlots`, the same fiscal-start-anchored slots the page's Period
 * filter offers, so a prefilled range always matches one of them.
 *
 * Never leaves the selected financial year: a cadence with nothing completed
 * inside it yet — half yearly and yearly early in the year — falls back to the
 * running period, spanning that period in full even though its end is still in
 * the future, because the figure belongs to the whole period. A financial year
 * that has not started yet prefills nothing at all.
 *
 * Single source of truth for the default, so the modal's initial state and its
 * reset-on-open can never disagree. CUSTOM yields null by design: its boundaries
 * are not derivable, so the data owner types the range instead.
 *
 * @param {string} frequency the KPI's effective cadence (`answerFrequency`).
 * @param {string} fyLabel financial-year label such as "2026-2027".
 * @param {number} startingMonth 1-based fiscal start month.
 * @param {string} [today] `YYYY-MM-DD` to resolve "now" against.
 * @returns {{fromDate: string, toDate: string}|null} the range, or null when none applies.
 */
export function defaultPeriodForFrequency(frequency, fyLabel, startingMonth, today = todayISO()) {
  const options = reportablePeriodOptions(frequency, fyLabel, startingMonth, today);
  if (!options.length) return null;
  const last = options[options.length - 1];
  return { fromDate: last.fromDate, toDate: last.toDate };
}

/**
 * The periods a data owner may report against, for the KPI's configured cadence.
 *
 * Only periods that have fully ended are offered: on 14 Aug with an April fiscal
 * start, a monthly KPI lists APR through JUL and not the running August, because
 * a month in progress has no complete figure to enter. Quarterly, half yearly and
 * yearly follow the same rule against their own boundaries.
 *
 * A cadence with nothing completed inside the year yet — yearly, or half yearly
 * in the first six months — would otherwise offer nothing and block entry, so it
 * falls back to the single running period. That period carries its own full
 * boundaries, not a range truncated at today: a figure is filed against the
 * period the cadence defines, so a yearly KPI reports the whole financial year
 * even while the year is still open. Such an option is flagged `partial` so the
 * form can say the period has not ended, without narrowing what is submitted.
 * The last entry is therefore always the period Add Data prefills, which is what
 * makes {@link defaultPeriodForFrequency} a thin wrapper over this list.
 *
 * Boundaries come from `buildPeriodSlots`, the same generator behind the page's
 * Period filter and the Triggers screen, so a submitted range always lines up
 * with the periods those screens report on. Only the labels are local — those
 * screens name slots "Q1 (Apr 2026 - Jun 2026)" where a dropdown needs the
 * compact "Q1 (APR-JUN 2026)".
 *
 * CUSTOM yields nothing by design: its boundaries are not derivable, so the data
 * owner types a range instead. Nor does a financial year that has not started.
 * @param {string} frequency the KPI's effective cadence (`answerFrequency`).
 * @param {string} fyLabel financial-year label such as "2026-2027".
 * @param {number} startingMonth 1-based fiscal start month.
 * @param {string} [today] `YYYY-MM-DD` to resolve "now" against.
 * @returns {Array<{value: string, label: string, fromDate: string, toDate: string, partial: boolean}>}
 *          the selectable periods, earliest first.
 */
export function reportablePeriodOptions(frequency, fyLabel, startingMonth, today = todayISO()) {
  if (!frequency || frequency === "CUSTOM" || !fyLabel || !startingMonth) return [];

  const slots = buildPeriodSlots(fyLabel, startingMonth)[frequency];
  if (!slots?.length) return [];

  const completed = slots.filter((slot) => slot.toDate <= today);
  if (completed.length) {
    return completed.map((slot, index) => ({
      value: periodValue(slot.fromDate, slot.toDate),
      label: periodLabel(frequency, index, slot.fromDate, slot.toDate),
      fromDate: slot.fromDate,
      toDate: slot.toDate,
      partial: false,
    }));
  }

  const runningIndex = slots.findIndex((slot) => slot.fromDate <= today && today < slot.toDate);
  if (runningIndex === -1) return [];
  const running = slots[runningIndex];
  return [{
    value: periodValue(running.fromDate, running.toDate),
    label: periodLabel(frequency, runningIndex, running.fromDate, running.toDate),
    fromDate: running.fromDate,
    toDate: running.toDate,
    partial: true,
  }];
}

/**
 * The option key for a period, carrying its own boundaries so a `<select>` can
 * map a chosen value straight back to a date range.
 * @param {string} fromDate `YYYY-MM-DD` start of the period.
 * @param {string} toDate `YYYY-MM-DD` end of the period.
 * @returns {string} the option value.
 */
function periodValue(fromDate, toDate) {
  return `${fromDate}|${toDate}`;
}

/**
 * A period's dropdown label: "APR 2026" for a month, "Q1 (APR-JUN 2026)" for a
 * quarter, "H1 (APR-SEP 2026)" for a half, "FY 2026-2027" for a year.
 *
 * A span crossing new year prints both years — "H2 (OCT 2026-MAR 2027)" — so a
 * period is never ambiguous about which year it ends in.
 * @param {string} frequency the cadence the period belongs to.
 * @param {number} index zero-based position of the period within the year.
 * @param {string} fromDate `YYYY-MM-DD` start of the period.
 * @param {string} toDate `YYYY-MM-DD` end of the period.
 * @returns {string} the label.
 */
function periodLabel(frequency, index, fromDate, toDate) {
  const from = monthParts(fromDate);
  const to = monthParts(toDate);

  if (frequency === "MONTHLY") return `${from.month} ${from.year}`;
  if (frequency === "YEARLY") {
    return from.year === to.year ? `FY ${from.year}` : `FY ${from.year}-${to.year}`;
  }

  const span = from.year === to.year
    ? `${from.month}-${to.month} ${to.year}`
    : `${from.month} ${from.year}-${to.month} ${to.year}`;
  return `${PERIOD_PREFIX[frequency] ?? ""}${index + 1} (${span})`;
}

/**
 * Splits an ISO date into the pieces a label needs.
 * @param {string} isoDate a `YYYY-MM-DD` date.
 * @returns {{month: string, year: string}} uppercase month abbreviation and year.
 */
function monthParts(isoDate) {
  const [year, month] = String(isoDate).split("-");
  return { month: MONTHS[Number(month) - 1] ?? "", year };
}
